import { useCallback, useState } from 'react';
import {
  Stack,
  Text,
  Secondary,
  Inline,
  InlineItem,
  H3,
  UtilityButton,
  Tooltip,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import { formatDistanceStrings, formatDateString } from '@/date';
import { ConnectorCreateModal } from './connector-create-modal';
import { ConnectorSecretConfirmation } from './connector-secret-confirmation';
import { ConnectorDeleteConfirmation } from './connector-delete-confirmation';
import type {
  ServiceAccountOauthClient,
  ServiceAccountOauthClientSecret,
} from '@/api/organisation';

type Props = {
  connectors?: ServiceAccountOauthClient[];
  now: string;
  isCreating?: boolean;
  newConnector?: ServiceAccountOauthClientSecret;
  onCreate?: (name: string, redirectUris: string[]) => void;
  onDelete?: (clientId: string) => void;
  onDismissNewConnector?: () => void;
};

const ServiceAccountConnectors = ({
  connectors = [],
  now,
  isCreating = false,
  newConnector,
  onCreate = noop,
  onDelete = noop,
  onDismissNewConnector = noop,
}: Props) => {
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState<
    ServiceAccountOauthClient | undefined
  >();

  const onCreateConnector = useCallback(
    (name: string, redirectUris: string[]) => {
      setShowCreate(false);
      onCreate(name, redirectUris);
    },
    [onCreate],
  );

  return (
    <>
      {showCreate && (
        <ConnectorCreateModal
          onCancel={() => setShowCreate(false)}
          onCreate={onCreateConnector}
          isCreating={isCreating}
        />
      )}
      {newConnector && (
        <ConnectorSecretConfirmation
          credential={newConnector}
          onDismiss={onDismissNewConnector}
        />
      )}
      {deleting && (
        <ConnectorDeleteConfirmation
          name={deleting.name ?? deleting.clientId}
          onDelete={() => onDelete(deleting.clientId)}
          onClose={() => setDeleting(undefined)}
        />
      )}
      <Stack space='medium'>
        <Text>
          Connectors are AI tools (M365 Copilot, Gemini) authorised to sign
          users in to Kinesis on this service account&rsquo;s behalf. Each
          connector has its own client ID and secret.
        </Text>
        {connectors.length === 0 ? (
          <Text>
            <Secondary>No connectors yet.</Secondary>
          </Text>
        ) : (
          <Stack space='small'>
            {connectors.map((connector) => (
              <Inline key={connector.clientId}>
                <InlineItem sizing='fill-container'>
                  <Stack space='xsmall'>
                    <H3 noWrap>{connector.name ?? connector.clientId}</H3>
                    <Text>
                      <Secondary>
                        {connector.clientId}
                        {' · '}
                        Created{' '}
                        <Tooltip
                          title={formatDateString(connector.created)}
                          placement='top'
                        >
                          <span>
                            {formatDistanceStrings(now, connector.created)}
                          </span>
                        </Tooltip>
                        {connector.lastUsed ? (
                          <>
                            {' · '}
                            Last used{' '}
                            {formatDistanceStrings(now, connector.lastUsed)}
                          </>
                        ) : (
                          <>
                            {' · '}
                            <span
                              style={{
                                color: 'var(--color-text-warning, #b45309)',
                              }}
                            >
                              Never used
                            </span>
                          </>
                        )}
                      </Secondary>
                    </Text>
                  </Stack>
                </InlineItem>
                <InlineItem>
                  <Tooltip title='Delete connector' placement='top'>
                    <UtilityButton
                      icon='delete'
                      magnitude='small'
                      variant='danger'
                      onClick={() => setDeleting(connector)}
                    />
                  </Tooltip>
                </InlineItem>
              </Inline>
            ))}
          </Stack>
        )}
        <Inline>
          <InlineItem>
            <UtilityButton
              icon='plus'
              magnitude='small'
              onClick={() => setShowCreate(true)}
              disabled={isCreating}
            >
              Add connector
            </UtilityButton>
          </InlineItem>
        </Inline>
      </Stack>
    </>
  );
};

export { ServiceAccountConnectors };
